/**
 * Naming the refusal a document gives when it is open read-only.
 *
 * A draft launched with `ms-word:ofe|u|<url>` comes from a URL Word cannot save to, so
 * Word opens it read-only, offering an **Edit Anyway** button once the user tries to
 * type (never when the add-in does). The pane
 * loads and reads the document perfectly well; every write is refused. Left alone, that
 * refusal went back to the model as `Word API error: GeneralException` and the model
 * told the user "Word returned an API error" -- true, and no help at all, when the fix
 * is one click on a button the model cannot see.
 *
 * How a host recognises the state is its own business (Word's is `word/writeProbe.ts`).
 * `Office.context.document.mode` is no use: it reports "readWrite" in exactly this state
 * (docs/assumptions.md A-41).
 */

/**
 * Says how to make the button appear, not just to press it: Word shows the Edit Anyway
 * bar only when the *user* tries to type in the document. The add-in's own refused
 * writes do not raise it, so at the moment this message appears there is usually no
 * bar on screen to point at.
 *
 * Hedged on purpose. The detection proves the host is refusing every write, not why;
 * Edit Anyway is by far the likeliest reason for a Jylo draft, and the sentence stays
 * true if it was something else.
 */
export const EDIT_ANYWAY =
  "Word is not accepting changes to this document yet. Click in the document and start " +
  "typing: Word will show an Edit Anyway button above it. Click that, then ask again.";

/**
 * Thrown out of a tool executor to end the turn rather than returned to the model: the
 * model cannot click the button, and a message that has to pass through it arrives
 * paraphrased, if at all. `DraftChat` shows it as the turn's error via `describeError`,
 * which passes an Error's own message through.
 */
export class DocumentReadOnlyError extends Error {
  constructor(readonly hostError?: string) {
    super(EDIT_ANYWAY);
    this.name = "DocumentReadOnlyError";
  }
}
